import { useCallback, useContext, useMemo } from "react";
import { dashboardContext } from "../Dashboard";
import type { AccessLevelName } from "./PayoutAccessControlData";

export type PayoutPermission =
  | "dashboard.view"
  | "statements.view"
  | "transfers.view"
  | "transfers.create"
  | "transfers.approve"
  | "beneficiaries.view"
  | "beneficiaries.manage"
  | "beneficiaries.delete"
  | "users.view"
  | "users.manage"
  | "roles.view"
  | "roles.manage"
  | "permissions.view"
  | "permissions.manage"
  | "approvalRules.manage"
  | "accounts.view"
  | "accounts.create"
  | "reports.view"
  | "settings.manage";

export type ModuleLink = {
  label: string;
  to: string;
  permission: PayoutPermission;
  children?: ModuleLink[];
};

const moduleLinks: ModuleLink[] = [
  {
    label: "Dashboard",
    to: "/payouts/dashboard",
    permission: "dashboard.view",
  },
  {
    label: "Statements",
    to: "/payouts/statements",
    permission: "statements.view",
  },
  {
    label: "Transfers",
    to: "/payouts/transfers",
    permission: "transfers.view",
  },
  {
    label: "Beneficiaries",
    to: "/payouts/beneficiaries",
    permission: "beneficiaries.view",
  },
  {
    label: "Users & Roles",
    to: "/payouts/users",
    permission: "users.view",
    children: [
      {
        label: "Users",
        to: "/payouts/users",
        permission: "users.view",
      },
      {
        label: "Roles",
        to: "/payouts/roles",
        permission: "roles.view",
      },
      // Permissions is hidden for now. Keep the route/page available so this can be restored later.
      // {
      //   label: "Permissions",
      //   to: "/payouts/permissions",
      //   permission: "permissions.view",
      // },
    ],
  },
  {
    label: "Approval Workflows",
    to: "/payouts/approval-rules",
    permission: "approvalRules.manage",
  },
  // Reports is hidden for now. Keep the route/page available so this can be restored later.
  // {
  //   label: "Reports",
  //   to: "/payouts/reports",
  //   permission: "reports.view",
  //   children: [
  //     {
  //       label: "Payout Reports",
  //       to: "/payouts/reports",
  //       permission: "reports.view",
  //     },
  //     {
  //       label: "Payout Insights",
  //       to: "/payouts/reports/insights",
  //       permission: "reports.view",
  //     },
  //   ],
  // },
  // Settings + MFA enrollment (Administrator).
  {
    label: "Settings",
    to: "/payouts/settings",
    permission: "settings.manage",
  },
];

const accessLevelRank: Record<AccessLevelName, number> = {
  Observer: 1,
  "Finance Executive": 2,
  "Finance Manager": 3,
  Administrator: 4,
};

const permissionMinimumRank: Record<PayoutPermission, number> = {
  "dashboard.view": 1,
  "statements.view": 1,
  "transfers.view": 1,
  "transfers.create": 2,
  "transfers.approve": 4,
  "beneficiaries.view": 1,
  "beneficiaries.manage": 2,
  "beneficiaries.delete": 4,
  "users.view": 3,
  "users.manage": 3,
  "roles.view": 2,
  "roles.manage": 4,
  "permissions.view": 3,
  "permissions.manage": 4,
  "approvalRules.manage": 4,
  "accounts.view": 1,
  "accounts.create": 4,
  "reports.view": 1,
  "settings.manage": 4,
};

const normalizeAccessLevel = (raw?: unknown): AccessLevelName | null => {
  const value = String(raw || "").trim().toLowerCase();
  if (!value) return null;
  if (value.includes("level 4") || value.includes("administrator") || value === "admin") {
    return "Administrator";
  }
  if (value.includes("level 3") || value.includes("finance manager") || value.includes("manager")) {
    return "Finance Manager";
  }
  if (value.includes("level 2") || value.includes("finance executive") || value.includes("executive") || value.includes("initiator")) {
    return "Finance Executive";
  }
  if (value.includes("level 1") || value.includes("observer") || value.includes("viewer")) {
    return "Observer";
  }
  return null;
};

const getNested = (source: unknown, path: string[]) =>
  path.reduce<unknown>((current, key) => {
    if (!current || typeof current !== "object") return undefined;
    return (current as Record<string, unknown>)[key];
  }, source);

const decodeJwtClaims = (token?: string | null): Record<string, unknown> | null => {
  try {
    const payload = token?.split(".")[1];
    if (!payload) return null;
    const normalized = payload.replace(/-/g, "+").replace(/_/g, "/");
    return JSON.parse(
      window.atob(normalized.padEnd(Math.ceil(normalized.length / 4) * 4, "=")),
    ) as Record<string, unknown>;
  } catch {
    return null;
  }
};

const firstAccessLevelFrom = (values: unknown[]): AccessLevelName | null => {
  for (const value of values) {
    if (Array.isArray(value)) {
      const nested = firstAccessLevelFrom(value);
      if (nested) return nested;
    }
    const normalized = normalizeAccessLevel(value);
    if (normalized) return normalized;
  }
  return null;
};

const getCurrentPayoutAccessLevel = (dashboardUser: unknown): AccessLevelName => {
  const merchant =
    getNested(dashboardUser, ["getMerchantQuery"]) ||
    getNested(dashboardUser, ["getUserQuery"]) ||
    dashboardUser ||
    {};
  const payoutClaims = decodeJwtClaims(window.localStorage.getItem("payoutsAccessToken"));
  const merchantClaims = decodeJwtClaims(
    window.localStorage.getItem("merchantToken") || window.localStorage.getItem("token"),
  );
  const configuredLevel =
    process.env.REACT_APP_PAYOUT_ACCESS_LEVEL ||
    window.localStorage.getItem("payoutAccessLevel") ||
    window.localStorage.getItem("payoutsAccessLevel");

  return (
    firstAccessLevelFrom([
      configuredLevel,
      getNested(merchant, ["payout_access_level"]),
      getNested(merchant, ["payoutAccessLevel"]),
      getNested(merchant, ["ui_access_level"]),
      getNested(merchant, ["uiAccessLevel"]),
      getNested(merchant, ["access_level"]),
      getNested(merchant, ["accessLevel"]),
      getNested(merchant, ["payoutRole"]),
      getNested(merchant, ["payout_role"]),
      getNested(merchant, ["role"]),
      getNested(payoutClaims, ["access_level"]),
      getNested(payoutClaims, ["accessLevel"]),
      getNested(payoutClaims, ["ui_access_level"]),
      getNested(payoutClaims, ["uiAccessLevel"]),
      getNested(payoutClaims, ["role"]),
      getNested(payoutClaims, ["roles"]),
      getNested(merchantClaims, ["payout_access_level"]),
      getNested(merchantClaims, ["payoutAccessLevel"]),
      getNested(merchantClaims, ["access_level"]),
      getNested(merchantClaims, ["accessLevel"]),
      getNested(merchantClaims, ["role"]),
      getNested(merchantClaims, ["roles"]),
    ]) || "Administrator"
  );
};

export const canAccessPayoutPermission = (
  accessLevel: AccessLevelName,
  permission: PayoutPermission,
) => accessLevelRank[accessLevel] >= permissionMinimumRank[permission];

const getVisibleModuleLinks = (accessLevel: AccessLevelName): ModuleLink[] =>
  moduleLinks
    .map((item) => {
      const children = item.children?.filter((child) =>
        canAccessPayoutPermission(accessLevel, child.permission),
      );
      if (item.children?.length) {
        if (!children?.length) return null;
        return {
          ...item,
          to: children[0].to,
          permission: children[0].permission,
          children,
        };
      }
      return canAccessPayoutPermission(accessLevel, item.permission) ? item : null;
    })
    .filter((item): item is ModuleLink => Boolean(item));

export const usePayoutAccess = () => {
  const { user } = useContext(dashboardContext) || {};
  const accessLevel = useMemo(() => getCurrentPayoutAccessLevel(user), [user]);
  const canAccess = useCallback(
    (permission: PayoutPermission) =>
      canAccessPayoutPermission(accessLevel, permission),
    [accessLevel],
  );
  const visibleLinks = useMemo(
    () => getVisibleModuleLinks(accessLevel),
    [accessLevel],
  );

  return { accessLevel, canAccess, visibleLinks };
};
